// Story 7.10 (ADO-906), AC1/AC3/AC5 — the attribute FilterPanel, pixel-ported to
// the designer prototype's full filters rail (Epic 7 re-skin, ADO-903).
//
// The prototype rail carries more sections than our read model can power today:
// Brand, Product Size and Country of Origin (server facets) and Department +
// Category (the live taxonomy) are DATA-BACKED. The "Show Me"/"Deals & Listing
// Type" checkboxes are facet-aware: an option `FACET_TOGGLES` maps to a facet value
// is a REAL server-filter toggle while its facet offers that value (or it is
// ticked). New Lines, New Listings, Own Brand, Mixed Case and Small Case map to
// flag facets (ADO-3692), and are disabled while their facet offers nothing; On
// Offer and Multi Buy map to the offer facets, which have no data yet, so they
// stay inert in practice. The rest render as faithful but
// INERT chrome (local visual state only; never sent to the API) until the work
// that owns their data lands (ADO-3692's deferral table): Handbill (Story 7.30),
// Value Projection and All Promotions (Epic 11, Story 7.31), RBP Core Lines
// (Story 18.12), Price/Margin/Price Marked/Alcohol RRP (Epic 11, Story 11.10),
// Top Sellers and My Regulars (no owner yet). Product decision 2026-07-16: "full
// rail, inert extras".
//
// Prototype anatomy (spec-epic7-category-listing-pixel-parity, task 3): Show Me →
// Deals & Listing Type → Department (collapsed) → Category (collapsed) → Brand
// (searchable SINGLE-select dropdown over the real brand facet) → Price & Margin
// (inert) → Product (Country of Origin select over the real countryOfOrigin facet,
// Product Size suggestions over the real packSize facet). The single-selects flow
// through the existing ProductFilters shape as one-element arrays (`{ brand: [value] }`).
//
// a11y (NFR24–26): sections are <fieldset>s with a <legend>; a collapsible legend
// carries the expand/collapse control with `aria-expanded`; multi-value rows are
// the design-system <Checkbox>/<RadioGroup> (native inputs → correct accessible
// name + keyboard operability); the search-selects are labelled inputs above a
// plain list of real buttons, the active value marked with `aria-current` (no
// listbox/option ARIA — see the SearchSelect note for why those roles were dropped).

import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Checkbox, Icon, RadioGroup } from '../../design-system';
import { FACET_TOGGLES, type Facet, type FacetKey, type ProductFilters } from '../../api/facets';
import type { CategoryNode } from '../../api/browse.client';
import { formatCategoryName } from './categoryDisplay';
import styles from './FilterPanel.module.css';

// ── The checkbox option sets, carried verbatim from the prototype's persona data
//    (src/lib/data/shoplink-2/category-listing.ts). An option `FACET_TOGGLES` maps
//    is sent to the API while live; the others are visual parity only. ──
const SHOW_ME_OPTIONS = ['On Offer', 'My Regulars', 'New Lines', 'RBP Core Lines'] as const;
const RBP_CORE_PERCENT = 78;
const DEAL_TYPE_OPTIONS = [
  'Top Sellers',
  'New Listings',
  'Own Brand',
  'Mixed Case',
  'Small Case',
  'Handbill',
  'Multi Buy',
  'Value Projection',
  'All Promotions',
] as const;
const ANY_DEPARTMENT = '__any__';
/** The flag facets the listing toggles map to (ADO-3692). */
const LISTING_FLAG_KEYS: ReadonlySet<FacetKey | undefined> = new Set<FacetKey>([
  'newListing',
  'ownBrand',
  'mixedCase',
  'smallCase',
]);

// The searchable dropdowns cap their visible result rows (prototype slice(0, 50)).
const MAX_DROPDOWN_RESULTS = 50;

export interface FilterPanelProps {
  /** The available server facets returned for the current node or search. */
  readonly facets: readonly Facet[];
  /** The currently-selected facet values per facet (host-owned). */
  readonly selected: ProductFilters;
  /** Toggle one value within a facet (add/remove). */
  readonly onToggleValue: (key: FacetKey, value: string) => void;
  /** Clear every active attribute filter, restoring the unfiltered set (AC4). */
  readonly onClearAll: () => void;
  /** True when at least one attribute filter is active. */
  readonly hasActive: boolean;
  /** All Departments (top-level nodes) for the Department radio group. Omitted on
   *  surfaces with no taxonomy context (e.g. keyword search) → section hidden. */
  readonly departments?: readonly CategoryNode[];
  /** The categories under the current department for the Category section. */
  readonly categories?: readonly CategoryNode[];
  /** The department the current listing sits under (root of the breadcrumb). */
  readonly currentDepartmentId?: string | null;
  /** The category the current listing sits under, when drilled that far. */
  readonly currentCategoryId?: string | null;
  /** Navigate to a taxonomy node (`null` = Shop root) — drives Department/Category. */
  readonly onNavigate?: (node: CategoryNode | null) => void;
}

export function FilterPanel({
  facets,
  selected,
  onToggleValue,
  onClearAll,
  hasActive,
  departments = [],
  categories = [],
  currentDepartmentId = null,
  currentCategoryId = null,
  onNavigate,
}: FilterPanelProps) {
  // Inert visual state for the not-yet-powered sections (never leaves the panel).
  const [showMe, setShowMe] = useState<readonly string[]>([]);
  const [dealTypes, setDealTypes] = useState<readonly string[]>([]);
  const inertActive = showMe.length > 0 || dealTypes.length > 0;

  const clearEverything = () => {
    onClearAll();
    setShowMe([]);
    setDealTypes([]);
  };

  const toggleInert = (setter: typeof setShowMe) => (value: string) =>
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const departmentRadioValue = currentDepartmentId ?? ANY_DEPARTMENT;
  const categoryCount = currentCategoryId === null ? 0 : 1;

  // The three real value facets, surfaced as prototype-style SINGLE-selects. The
  // wire shape stays the shared ProductFilters (one-element array), so the host
  // fetch/chips/clear plumbing is untouched.
  const brandFacet = facets.find((f) => f.key === 'brand');
  const packFacet = facets.find((f) => f.key === 'packSize');
  const countryFacet = facets.find((f) => f.key === 'countryOfOrigin');
  const selectedBrand = selected.brand?.[0] ?? null;
  const selectedPack = selected.packSize?.[0] ?? null;
  const selectedCountry = selected.countryOfOrigin?.[0] ?? null;
  const productCount = (selectedCountry === null ? 0 : 1) + (selectedPack === null ? 0 : 1);

  // Checkbox options with a live facet-value counterpart become REAL toggles
  // (server-filtered); options whose value the current facet response does not
  // offer stay inert chrome. Keyed by option label → (facet, value).
  const liveFacetFor = (label: string): { key: FacetKey; value: string } | null => {
    const mapping = FACET_TOGGLES[label];
    if (mapping === undefined) return null;
    const offered = facets
      .find((f) => f.key === mapping.key)
      ?.values.some((v) => v.value === mapping.value);
    // Keep the toggle live while its value is ACTIVE even if the (exclude-self)
    // facet momentarily stops offering it — the user must be able to un-tick.
    const active = selected[mapping.key]?.includes(mapping.value) ?? false;
    return offered === true || active ? mapping : null;
  };
  const isFacetChecked = (mapping: { key: FacetKey; value: string }) =>
    selected[mapping.key]?.includes(mapping.value) ?? false;
  // A listing toggle (ADO-3692) whose flag facet offers nothing in the current set is
  // disabled, not an inert tick: ticking it would look like a filter while sending
  // none. The offer toggles keep their inert fallback.
  const isUnofferedListingToggle = (label: string) =>
    LISTING_FLAG_KEYS.has(FACET_TOGGLES[label]?.key) && liveFacetFor(label) === null;
  const liveCheckedCount = (labels: readonly string[]) =>
    labels.reduce((n, label) => {
      const mapping = liveFacetFor(label);
      return n + (mapping !== null && isFacetChecked(mapping) ? 1 : 0);
    }, 0);

  /** Replace-semantics single-select over the existing toggle wiring: remove the
   *  previous value (if any), then add the next one. Both go through the host's
   *  functional set-state, so the two calls compose to `{ key: [value] }`. */
  const applySingle = (key: FacetKey, current: string | null) => (value: string | null) => {
    if (value === current) return;
    if (current !== null) onToggleValue(key, current);
    if (value !== null) onToggleValue(key, value);
  };

  return (
    <aside className={styles.panel} aria-label="Filters">
      <div className={styles.header}>
        <h2 className={styles.heading}>Filters</h2>
        {(hasActive || inertActive || currentCategoryId !== null) && (
          <button type="button" className={styles.clear} onClick={clearEverything}>
            Clear
          </button>
        )}
      </div>

      {/* Show Me: "New Lines" (newListing) and "On Offer" (promotionalStatus) are
          real toggles while their facets offer them; My Regulars and RBP Core
          Lines stay inert until their owners land their data. */}
      <StaticSection label="Show Me" badge={showMe.length + liveCheckedCount(SHOW_ME_OPTIONS)}>
        <ul className={styles.values}>
          {SHOW_ME_OPTIONS.map((opt) => {
            const mapping = liveFacetFor(opt);
            return (
              <li key={opt} className={styles.value}>
                <Checkbox
                  checked={mapping !== null ? isFacetChecked(mapping) : showMe.includes(opt)}
                  disabled={isUnofferedListingToggle(opt)}
                  onChange={() =>
                    mapping !== null
                      ? onToggleValue(mapping.key, mapping.value)
                      : toggleInert(setShowMe)(opt)
                  }
                  label={
                    <span className={styles.valueLabel}>
                      <span className={styles.valueName}>{opt}</span>
                      {opt === 'RBP Core Lines' && (
                        <span className={styles.count}>{RBP_CORE_PERCENT}%</span>
                      )}
                    </span>
                  }
                />
              </li>
            );
          })}
        </ul>
      </StaticSection>

      {/* Deals & Listing Type: New Listings, Own Brand, Mixed Case, Small Case
          (flag facets) and Multi Buy (offerType) are real toggles while their
          facets offer them; Top Sellers, Handbill, Value Projection and All
          Promotions stay inert until their owners land their data. */}
      <CollapsibleSection
        label="Deals & Listing Type"
        badge={dealTypes.length + liveCheckedCount(DEAL_TYPE_OPTIONS)}
        defaultExpanded
      >
        <ul className={styles.values}>
          {DEAL_TYPE_OPTIONS.map((opt) => {
            const mapping = liveFacetFor(opt);
            return (
              <li key={opt} className={styles.value}>
                <Checkbox
                  checked={mapping !== null ? isFacetChecked(mapping) : dealTypes.includes(opt)}
                  disabled={isUnofferedListingToggle(opt)}
                  onChange={() =>
                    mapping !== null
                      ? onToggleValue(mapping.key, mapping.value)
                      : toggleInert(setDealTypes)(opt)
                  }
                  label={<span className={styles.valueName}>{opt}</span>}
                />
              </li>
            );
          })}
        </ul>
      </CollapsibleSection>

      {/* Department — real taxonomy; selecting navigates (like a breadcrumb hop).
          Collapsed by default, per the prototype. */}
      {departments.length > 0 && (
        <CollapsibleSection label="Department" badge={currentDepartmentId === null ? 0 : 1}>
          <div className={styles.radioRows}>
            {/* No explicit `name`: CategoryListing mounts this panel twice (desktop
                rail + mobile FiltersDrawer), so a hardcoded name would merge both
                radio sets into one native group — arrow-key navigation would span
                both instances and their checked states would fight. RadioGroup falls
                back to a per-instance `useId` name (same fix as the section ids). */}
            <RadioGroup
              label="Department"
              value={departmentRadioValue}
              onChange={(value) => {
                if (value === ANY_DEPARTMENT) {
                  onNavigate?.(null);
                  return;
                }
                const dept = departments.find((d) => d.id === value);
                if (dept) onNavigate?.(dept);
              }}
              options={[
                { value: ANY_DEPARTMENT, label: 'Any' },
                ...departments.map((d) => ({ value: d.id, label: formatCategoryName(d.name) })),
              ]}
            />
          </div>
        </CollapsibleSection>
      )}

      {/* Category — real; single-active (selecting navigates into that category).
          Collapsed by default, per the prototype. */}
      {categories.length > 0 && (
        <CollapsibleSection label="Category" badge={categoryCount}>
          <ul className={styles.values}>
            {categories.map((cat) => {
              const isActive = cat.id === currentCategoryId;
              return (
                <li key={cat.id} className={styles.value}>
                  <Checkbox
                    checked={isActive}
                    onChange={() => onNavigate?.(isActive ? null : cat)}
                    label={<span className={styles.valueName}>{formatCategoryName(cat.name)}</span>}
                  />
                </li>
              );
            })}
          </ul>
        </CollapsibleSection>
      )}

      {/* Brand — REAL server facet, prototype searchable single-select dropdown. */}
      {brandFacet !== undefined && (
        <fieldset className={styles.section}>
          <legend className={styles.staticLegend}>
            Brand
            {selectedBrand !== null && (
              <span className={styles.pill} aria-hidden="true">
                1
              </span>
            )}
          </legend>
          <SearchSelect
            inputLabel="Search brand"
            placeholder="Search brand…"
            values={brandFacet.values.map((v) => v.value)}
            selected={selectedBrand}
            onSelect={applySingle('brand', selectedBrand)}
          />
        </fieldset>
      )}

      {/* Price (€) / Margin — inert (no pricing on the read model until Epic 8).
          Two sub-collapsibles, both open, with static range-slider chrome. */}
      <div className={styles.section}>
        <InertSubCollapsible label="Price (€)">
          <RangeChrome />
          <p className={styles.rangeValue} aria-hidden="true">
            €0.0 – €50.0
          </p>
        </InertSubCollapsible>
        <InertSubCollapsible label="Margin">
          <RangeChrome />
          <ul className={styles.values}>
            <li className={styles.value}>
              <Checkbox
                disabled
                label={<span className={styles.valueName}>Margin &gt; 30%</span>}
              />
            </li>
            <li className={styles.value}>
              <Checkbox disabled label={<span className={styles.valueName}>Price Marked</span>} />
            </li>
            <li className={styles.value}>
              <Checkbox disabled label={<span className={styles.valueName}>Alcohol RRP</span>} />
            </li>
          </ul>
        </InertSubCollapsible>
      </div>

      {/* Product: REAL Country of Origin select (the countries in the current
          set, with counts) + REAL Product Size suggestions. The pill counts both,
          as Brand's counts its one (ADO-3692, AC2). Hidden, as Brand is, when the
          set offers neither facet. */}
      {(countryFacet !== undefined || packFacet !== undefined) && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>
            Product
            {productCount > 0 && (
              <span className={styles.pill} aria-hidden="true">
                {productCount}
              </span>
            )}
          </p>
          {countryFacet !== undefined && (
            <label className={styles.fieldLabel}>
              Country of Origin
              <select
                className={styles.select}
                value={selectedCountry ?? ''}
                onChange={(e) =>
                  applySingle('countryOfOrigin', selectedCountry)(e.target.value || null)
                }
              >
                <option value="">Any</option>
                {countryFacet.values.map((v) => (
                  <option key={v.value} value={v.value}>
                    {`${v.value} (${v.count})`}
                  </option>
                ))}
              </select>
            </label>
          )}
          {packFacet !== undefined && (
            <div className={styles.fieldLabel}>
              <span>Product Size</span>
              <SearchSelect
                inputLabel="Product size"
                placeholder="e.g. 1Ltr, 330ml"
                values={packFacet.values.map((v) => v.value)}
                selected={selectedPack}
                onSelect={applySingle('packSize', selectedPack)}
              />
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

/** A non-collapsible section (fieldset + legend heading + applied-count pill). */
function StaticSection({
  label,
  badge = 0,
  children,
}: {
  label: string;
  badge?: number;
  children: ReactNode;
}) {
  return (
    <fieldset className={`${styles.section} ${styles.firstSection}`}>
      <legend className={styles.staticLegend}>
        {label}
        {badge > 0 && (
          <span className={styles.pill} aria-hidden="true">
            {badge}
          </span>
        )}
      </legend>
      {children}
    </fieldset>
  );
}

/** A collapsible section: legend button with a chevron + optional count pill. */
function CollapsibleSection({
  label,
  badge = 0,
  defaultExpanded = false,
  children,
}: {
  label: string;
  badge?: number;
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // useId (not a label-derived slug): the panel renders twice on small screens
  // (rail + drawer), so slugs would collide.
  const contentId = useId();
  return (
    <fieldset className={styles.section}>
      <legend className={styles.legend}>
        <button
          type="button"
          className={styles.legendButton}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded((v) => !v)}
        >
          <span className={styles.legendLabel}>
            {label}
            {badge > 0 && (
              <span className={styles.pill} aria-hidden="true">
                {badge}
              </span>
            )}
          </span>
          <Icon name={expanded ? 'caretDown' : 'caretRight'} size={18} className={styles.chevron} />
        </button>
      </legend>
      <div id={contentId} hidden={!expanded}>
        {children}
      </div>
    </fieldset>
  );
}

/** An inert sub-collapsible (Price (€) / Margin) — open by default, chrome only. */
function InertSubCollapsible({ label, children }: { label: string; children: ReactNode }) {
  const [expanded, setExpanded] = useState(true);
  const contentId = useId();
  return (
    <div className={styles.subSection}>
      <button
        type="button"
        className={styles.legendButton}
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={styles.legendLabel}>{label}</span>
        <Icon name={expanded ? 'caretDown' : 'caretRight'} size={18} className={styles.chevron} />
      </button>
      <div id={contentId} hidden={!expanded}>
        {children}
      </div>
    </div>
  );
}

/** Static dual-thumb range-slider chrome (visual only — no pricing data yet). */
function RangeChrome() {
  return (
    <div className={styles.rangeRow} aria-hidden="true">
      <span className={styles.rangeTrack}>
        <span className={styles.rangeFillBar} />
        <span className={`${styles.rangeKnob} ${styles.rangeKnobStart}`} />
        <span className={`${styles.rangeKnob} ${styles.rangeKnobEnd}`} />
      </span>
    </div>
  );
}

interface SearchSelectProps {
  /** Accessible name of the text input (and its dropdown listbox). */
  readonly inputLabel: string;
  readonly placeholder: string;
  /** The full candidate values (real facet values from the API). */
  readonly values: readonly string[];
  readonly selected: string | null;
  /** `null` clears the selection. */
  readonly onSelect: (value: string | null) => void;
}

/** Prototype searchable single-select: a small input whose dropdown lists the
 *  matching facet values (max 50), with a "Clear selection" row when one is
 *  active. Clearing the input text also clears the selection (prototype parity). */
function SearchSelect({ inputLabel, placeholder, values, selected, onSelect }: SearchSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on an outside pointer-down (instead of the prototype's blur+setTimeout,
  // which races the option click).
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const matches = (q === '' ? values : values.filter((v) => v.toLowerCase().includes(q))).slice(
    0,
    MAX_DROPDOWN_RESULTS,
  );

  const choose = (value: string | null) => {
    onSelect(value);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className={styles.searchSelect} ref={rootRef}>
      <input
        type="text"
        className={styles.searchInput}
        aria-label={inputLabel}
        placeholder={placeholder}
        value={query === '' ? (selected ?? '') : query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (e.target.value === '') onSelect(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          // Only swallow Escape when THIS dropdown is open, and stop it there
          // (PR #139 review): on mobile FilterPanel is the FiltersDrawer body, and
          // the drawer closes on a document-level Escape — without this, one Escape
          // meant to dismiss the open dropdown would also collapse the whole drawer
          // (mirrors SubstitutesPanel). A closed dropdown lets Escape bubble so the
          // drawer can still be dismissed from the input.
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
            setQuery('');
          }
        }}
      />
      {open && (matches.length > 0 || (selected !== null && query === '')) && (
        // PR #139 review: a labelled list of real buttons, NOT an ARIA listbox —
        // the `<li>` wrappers broke listbox `aria-required-children` and there's no
        // listbox keyboard model (arrow keys / aria-activedescendant) to back the
        // roles. Same treatment as the SearchTypeahead dropdown; the active value is
        // marked with `aria-current` (was the dropped `role=option`/`aria-selected`).
        <ul className={styles.dropdown} aria-label={inputLabel}>
          {selected !== null && query === '' && (
            <li>
              <button
                type="button"
                className={`${styles.option} ${styles.optionMuted}`}
                onClick={() => choose(null)}
              >
                Clear selection
              </button>
            </li>
          )}
          {matches.map((v) => (
            <li key={v}>
              <button
                type="button"
                aria-current={selected === v ? 'true' : undefined}
                className={`${styles.option} ${selected === v ? styles.optionSelected : ''}`}
                onClick={() => choose(v)}
              >
                {v}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
